import { localStorage as safeLocalStorage } from 'utils/browserStorage';
import React, { useState, useEffect } from 'react'
import SignUpHeader from "../../../components/SignUpHeader";
import HeaderStatusBar from "../../../components/HeaderStatusBar";
import { FormMaterialInput } from 'components/Inputs/MaterialInput/FormMaterialInput';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import { IconButton, InputAdornment } from '@mui/material';
import { Page, Container, PopupContainer, PopupDescription, PopupTitle } from './ResetPassword.styles';

import { useForm } from 'react-hook-form';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { joiResolver } from '@hookform/resolvers/joi';
import joi from "joi";
import Api from "api";
import Popup from 'components/Popup';
import { useActionApi } from 'hooks';
import FixedLoader from 'components/FixedLoader';

export const schema = joi
    .object({
        password: joi.string().min(6).label('New Password').required(),
        repeatPassword: joi.string().custom((value: string, helper: any) => {
            const { password } = helper.state.ancestors[0]
            return value === password ? value : helper.message('Passwords don\'t match')
        }).required(),
    })
    .required()

export const isResetPasswordUpdateDisabled = (password?: string, repeatPassword?: string) => (
    !password || password !== repeatPassword || password.length < 6
);

interface PasswordUpdatedModalProps {
    open: boolean;
    onClose: () => void;
}

const PasswordUpdatedModal: React.FC<PasswordUpdatedModalProps> = ({ open, onClose }) => {
    return (
        <Popup opened={open} onClose={onClose}>
            <PopupContainer>
                <PopupTitle>Password has been changed successfully</PopupTitle>
                <PopupDescription>You can now log in to your account.</PopupDescription>
                <StyledButton
                    variant="contained"
                    onClick={onClose}
                    color="primary"
                    size="large"
                    id="review-button"
                >
                    OK
                </StyledButton>
            </PopupContainer>
        </Popup>
    );
};

export default function ResetPassword() {
    const {
        control,
        handleSubmit,
        formState: { errors },
        watch,
    } = useForm({
        mode: "onChange",
        resolver: joiResolver(schema) as any,
    });
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [isOpenPasswordUpdated, setIsOpenPasswordUpdated] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    useEffect(() => {
        const token = window?.location.search.match(/resetPasswordToken=([^&]+)/)?.[1];
        if (token) {
            safeLocalStorage.removeItem('userAuth');
            safeLocalStorage.removeItem('userAccount');
            // Stale JWT on the shared Api instance would make validateTokenVersion reject
            // POST /tsusers/resetPassword before the handler runs (T2-6012).
            Api.setAuthJwtToken(undefined);

            window.history.pushState({}, '', window.location.pathname + window.location.search);
        } else {
            window.location.href = '/';
        }
    }, []);

    const [resetPasswordLoading, , resetPassword] = useActionApi({
        apiMethod: Api.resetPassword,
        onSuccess: () => {
            setIsOpenPasswordUpdated(true);
        },
        onError: (error: string) => {
            setError('Password reset failed: ' + error);
        }
    })

    const onSubmit = async ({ password }: any) => {
        const data = {
            password,
            token: window?.location.search.match(/resetPasswordToken=(.+)/)?.[1] || ''
        }

        resetPassword(data)
    }

    const onBack = () => {
        window.location.href = '/'
    }

    const handleClickShowPassword = () => {
        setShowPassword((show) => !show);
    }

    const password = watch('password');
    const repeatPassword = watch('repeatPassword');

    const disabledUpdate = isResetPasswordUpdateDisabled(password, repeatPassword);

    return (
        <Page>
            <Container data-page="role-select" className={'no-swipeback'}>
                <HeaderStatusBar onButton={onBack} buttonText={'Back'} withArrow />

                <PasswordUpdatedModal
                    onClose={() => {
                        setIsOpenPasswordUpdated(false)
                        onBack()
                    }}
                    open={isOpenPasswordUpdated}
                />

                <div className={"loginForm"}>
                    <SignUpHeader
                        title={'Reset Password'}
                        error={error.length ? error : undefined}
                    />

                    <div className="contentBlock">
                        <form id="login-form" onSubmit={handleSubmit(onSubmit)}>
                            <FormMaterialInput
                                control={control}
                                className={'inset'}
                                label="New Password (at least 6 characters)"
                                name={"password"}
                                type={showPassword ? 'text' : 'password'}
                                endAdornment={
                                    <InputAdornment position="end">
                                        <IconButton
                                            className={'no-fastclick'}
                                            aria-label="visibility"
                                            onClick={handleClickShowPassword}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                }
                            />
                            <FormMaterialInput
                                className={'inset'}
                                control={control}
                                name={'repeatPassword'}
                                label={'Repeat Password'}
                                type={'password'}
                            />
                            <StyledButton
                                disabled={disabledUpdate}
                                variant="contained"
                                color="primary"
                                size="large"
                                id="signup-button"
                                type={'submit'}
                            >
                                Update
                            </StyledButton>
                        </form>
                    </div>

                    <div className="terms-of-service-privacy-pol-wrapper">
                        <div className="terms-of-service-container">
                            <a className="underlinedLink external externalLink"
                                href="https://www.tempstars.ca/terms"
                                target="_system"
                            >
                                Terms of Service
                            </a>
                            <span className="span1"> & </span>
                            <a className="underlinedLink external externalLink"
                                href="https://www.tempstars.ca/privacy"
                                target="_system"
                            >
                                Privacy Policy
                            </a>
                        </div>
                    </div>
                </div>

                <div className="footer footerLinks"></div>
            </Container>

            <FixedLoader loading={resetPasswordLoading} />
        </Page>
    )
}
